import { RootState } from "common/reducers";
import { EstimateActions } from "common/reducers/estimate.actions";
import { EstimateGroupActions } from "common/reducers/estimateGroup.actions";
import { estimateGroupsReducer } from "common/reducers/estimateGroup.reducer";
import { EstimateLineItemActions } from "common/reducers/estimateLineItem.actions";
import { estimateLineItemsReducer } from "common/reducers/estimateLineItem.reducer";
import { EstimateLineItemOptionActions } from "common/reducers/estimateLineItemOption.actions";
import { estimateLineItemOptionsReducer } from "common/reducers/estimateLineItemOption.reducer";
import { reducer as estimateReducer } from "common/reducers/estimate.reducer";
import { Map, Record } from "immutable";
import { estimateLineItemOption } from "common/selectors/estimateLineItemOption.selectors";
import { estimateLineItem } from "common/selectors/estimateLineItem.selectors";
import { estimateGroup } from "common/selectors/estimateGroup.selectors";
import { Nullable } from "common/records";

export type TreeNodeType =
  | "option"
  | "lineItem"
  | "group"
  | "subtotals"
  | "discountTotals"
  | "taxTotals"
  | "paymentTermsTotals"
  | "financeOptionPayments";
export interface TreeNode {
  parentIdField?: string;
  childrenIdField?: string;
  parentNodeType?: TreeNodeType;
  calculator: (state: RootState, id: number) => RootState;
}

const TREE_HIERARCHY: Map<TreeNodeType, TreeNode> = Map({
  option: {
    parentIdField: "estimate_line_item_id",
    childrenIdField: undefined,
    parentNodeType: "lineItem",
    calculator: (state: RootState, id: number) =>
      estimateLineItemOptionsReducer(state, EstimateLineItemOptionActions.updateTotals(id)),
  },
  lineItem: {
    parentIdField: "estimate_group_id",
    childrenIdField: "option_ids",
    parentNodeType: "group",
    calculator: (state: RootState, id: number) =>
      estimateLineItemsReducer(state, EstimateLineItemActions.updateTotals(id)),
  },
  group: {
    parentIdField: "estimate_id",
    childrenIdField: "line_item_ids",
    parentNodeType: "subtotals",
    calculator: (state: RootState, id: number) => estimateGroupsReducer(state, EstimateGroupActions.updateTotals(id)),
  },
  subtotals: {
    parentIdField: undefined,
    childrenIdField: "group_ids",
    parentNodeType: "discountTotals",
    calculator: (state: RootState, id: number) => estimateReducer(state, EstimateActions.updateSubtotals(id)),
  },
  discountTotals: {
    parentIdField: undefined,
    childrenIdField: undefined,
    parentNodeType: "taxTotals",
    calculator: (state: RootState, id: number) => estimateReducer(state, EstimateActions.updateDiscountTotals(id)),
  },
  taxTotals: {
    parentIdField: undefined,
    childrenIdField: undefined,
    parentNodeType: "paymentTermsTotals",
    calculator: (state: RootState, id: number) => estimateReducer(state, EstimateActions.updateTaxTotals(id)),
  },
  paymentTermsTotals: {
    parentIdField: undefined,
    childrenIdField: undefined,
    parentNodeType: "financeOptionPayments",
    calculator: (state: RootState, id: number) => estimateReducer(state, EstimateActions.updatePaymentTermsTotals(id)),
  },
  financeOptionPayments: {
    parentIdField: undefined,
    childrenIdField: undefined,
    parentNodeType: undefined,
    calculator: (state: RootState, id: number) =>
      estimateReducer(state, EstimateActions.updateEstimateFinanceOptionPayments(id)),
  },
});

export const updateNodeAndParents = (state: RootState, nodeType: TreeNodeType, nodeId: number): RootState => {
  let currentState = state;

  // Get the node configuration
  const nodeConfig = TREE_HIERARCHY.get(nodeType);
  if (!nodeConfig) return state;

  // Update the current node
  currentState = nodeConfig.calculator(currentState, nodeId);

  // If this node has a parent node type, recursively update up the tree
  if (nodeConfig.parentNodeType) {
    const parentId = getParentId(currentState, nodeType, nodeId, nodeConfig.parentIdField);

    currentState = updateNodeAndParents(currentState, nodeConfig.parentNodeType, parentId);
  }

  return currentState;
};

const getParentId = (state: RootState, nodeType: TreeNodeType, nodeId: number, parentIdField: any) => {
  let record: Nullable<Record<any>>;
  switch (nodeType) {
    case "option":
      record = estimateLineItemOption(state, { estimateLineItemOptionId: nodeId });
      break;
    case "lineItem":
      record = estimateLineItem(state, { estimateLineItemId: nodeId });
      break;
    case "group":
      record = estimateGroup(state, { estimateGroupId: nodeId });
      break;
    case "subtotals":
    case "discountTotals":
    case "taxTotals":
    case "paymentTermsTotals":
    case "financeOptionPayments":
      return nodeId;
    default:
      return null;
  }

  if (!record) {
    return null;
  }
  return record.get(parentIdField);
};
